<template>
  <div class="w-full h-full overflow-y-auto">
    <!-- Header -->
    <div class="bg-gradient-to-r from-content-800 to-content-700 p-4 sm:p-6 rounded-lg mb-4 sm:mb-6 flex-shrink-0">
      <h1 class="text-xl sm:text-2xl font-bold text-white">
        Oportunidades de Inversión
      </h1>
      <p class="mt-2 text-xs sm:text-sm text-content-200">
        Descubre las mejores oportunidades de inversión inmobiliaria disponibles
      </p>
    </div>

    <div class="pb-4">
      <!-- Filters -->
      <OpportunityFilters 
        :filters="filters"
        @filter-change="handleFilterChange"
      />

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <loader-icon class="h-10 w-10 text-primary-500"></loader-icon>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredOpportunities.length === 0" class="text-center py-12">
        <div class="max-w-md mx-auto bg-gradient-to-r from-content-800 to-content-700 p-6 rounded-lg">
          <svg class="mx-auto h-12 w-12 text-content-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-white">No hay oportunidades disponibles</h3>
          <p class="mt-1 text-sm text-content-200">
            {{ hasActiveFilters ? 'Intenta ajustar los filtros de búsqueda' : 'No hay oportunidades de inversión actualmente' }}
          </p>
        </div>
      </div>

      <!-- Opportunities Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <OpportunityCard
          v-for="opportunity in paginatedOpportunities"
          :key="opportunity.id"
          :opportunity="opportunity"
          @click="viewOpportunityDetail"
          @invest="handleInvestment"
        />
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="mt-8 flex justify-center">
        <nav class="flex items-center space-x-2 bg-gradient-to-r from-content-800 to-content-700 p-4 rounded-lg">
          <button
            @click="currentPage = Math.max(1, currentPage - 1)"
            :disabled="currentPage === 1"
            class="px-3 py-2 text-sm font-medium text-white bg-content-600 border border-content-500 rounded-md hover:bg-content-500 disabled:opacity-50 disabled:cursor-not-allowed smooth"
          >
            Anterior
          </button>
          
          <span class="px-3 py-2 text-sm text-white">
            Página {{ currentPage }} de {{ totalPages }}
          </span>
          
          <button
            @click="currentPage = Math.min(totalPages, currentPage + 1)"
            :disabled="currentPage === totalPages"
            class="px-3 py-2 text-sm font-medium text-white bg-content-600 border border-content-500 rounded-md hover:bg-content-500 disabled:opacity-50 disabled:cursor-not-allowed smooth"
          >
            Siguiente
          </button>
        </nav>
      </div>

      <!-- Load More (Alternative to pagination for mobile) -->
      <div v-if="showLoadMore" class="mt-8 text-center md:hidden">
        <button
          @click="loadMore"
          :disabled="loading"
          class="w-full sm:w-auto primary-button disabled:opacity-50"
        >
          {{ loading ? 'Cargando...' : 'Cargar más oportunidades' }}
        </button>
      </div>
    </div>

    <!-- Investment Modal -->
    <InvestmentModal
      v-if="showInvestmentModal"
      :opportunity="selectedOpportunity"
      @close="showInvestmentModal = false"
      @submit="submitInvestment"
    />
  </div>
</template>

<script>
import api from "../../../api";
import OpportunityCard from "../../../components/opportunities/OpportunityCard.vue";
import OpportunityFilters from "../../../components/opportunities/OpportunityFilters.vue";
import InvestmentModal from "../../../components/opportunities/InvestmentModal.vue";

export default {
  name: "Opportunities",
  components: {
    OpportunityCard,
    OpportunityFilters,
    InvestmentModal
  },
  data() {
    return {
      loading: false,
      opportunities: [],
      filteredOpportunities: [],
      myIntentions: [],
      filters: {
        search: '',
        instrument: '',
        investmentRange: '',
        sortBy: 'newest',
        minRate: null,
        maxRate: null,
        maxTerm: '',
        interestPayment: ''
      },
      currentPage: 1,
      itemsPerPage: 9,
      showInvestmentModal: false,
      selectedOpportunity: null
    };
  },

  computed: {
    hasActiveFilters() {
      return Object.values(this.filters).some(value => 
        value !== '' && value !== null && value !== undefined
      );
    },
    totalPages() {
      return Math.ceil(this.filteredOpportunities.length / this.itemsPerPage);
    },
    paginatedOpportunities() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      const mapped = this.filteredOpportunities.slice(start, end).map(opportunity => {
        const hasIntention = this.hasIntentionForOpportunity(opportunity.id);
        const intentionStatus = this.getIntentionStatus(opportunity.id);
        return {
          ...opportunity,
          hasIntention,
          intentionStatus
        };
      });
      return mapped;
    },
    showLoadMore() {
      return this.currentPage < this.totalPages;
    }
  },

  async created() {
    this.$store.commit("setSelectedTab", "opportunities");
    await this.loadOpportunities();
    await this.loadMyIntentions();
  },

  methods: {
    async loadOpportunities() {
      this.loading = true;

      try {
        const response = await api.opportunities.getClientOpportunities();
        // The backend returns { data: [...], pagination: {...} }
        this.opportunities = response.data || [];
        this.applyFilters();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al cargar oportunidades");
      }

      this.loading = false;
    },

    async loadMyIntentions() {
      // Check if user is authenticated
      if (!this.$store.state.user || this.$store.state.user.type !== 'client') {
        return;
      }

      try {
        const response = await api.opportunities.getMyIntentions();
        this.myIntentions = response.data || [];
      } catch (error) {
        console.error('Error loading intentions:', error);

        if (error.response?.status === 401) {
          // Don't redirect - let the user stay logged in
        }
      }
    },

    hasIntentionForOpportunity(opportunityId) {
      return this.myIntentions.some(intention => 
        intention.opportunityId === opportunityId && 
        ['pending', 'confirmed'].includes(intention.status)
      );
    },

    getIntentionStatus(opportunityId) {
      const intention = this.myIntentions.find(intention => 
        intention.opportunityId === opportunityId && 
        ['pending', 'confirmed'].includes(intention.status)
      );
      return intention ? intention.status : null;
    },

    handleFilterChange(newFilters) {
      this.filters = { ...newFilters };
      this.currentPage = 1;
      this.applyFilters();
    },

    applyFilters() {
      let filtered = [...this.opportunities];

      if (this.filters.search) {
        const searchTerm = this.filters.search.toLowerCase();
        filtered = filtered.filter(opp => 
          opp.title.toLowerCase().includes(searchTerm) ||
          opp.description.toLowerCase().includes(searchTerm)
        );
      }

      if (this.filters.instrument) {
        filtered = filtered.filter(opp => opp.instrument === this.filters.instrument);
      }

      if (this.filters.investmentRange) {
        const [min, max] = this.filters.investmentRange.split('-').map(v => v === '' ? null : parseInt(v));
        filtered = filtered.filter(opp => {
          if (min !== null && opp.minimumInvestment < min) return false;
          if (max !== null && opp.minimumInvestment > max) return false;
          return true;
        });
      }

      if (this.filters.minRate !== null) {
        filtered = filtered.filter(opp => opp.annualFixedRate >= this.filters.minRate);
      }

      if (this.filters.maxRate !== null) {
        filtered = filtered.filter(opp => opp.annualFixedRate <= this.filters.maxRate);
      }

      if (this.filters.maxTerm) {
        const maxTermMonths = parseInt(this.filters.maxTerm);
        filtered = filtered.filter(opp => {
          const termMonths = this.extractMonthsFromTerm(opp.term);
          return termMonths <= maxTermMonths;
        });
      }

      if (this.filters.interestPayment) {
        filtered = filtered.filter(opp => opp.interestPayment === this.filters.interestPayment);
      }

      filtered = this.sortOpportunities(filtered, this.filters.sortBy);

      this.filteredOpportunities = filtered;
    },

    sortOpportunities(opportunities, sortBy) {
      return opportunities.sort((a, b) => {
        switch (sortBy) {
          case 'highestRate':
            return b.annualFixedRate - a.annualFixedRate;
          case 'shortestTerm':
            return this.extractMonthsFromTerm(a.term) - this.extractMonthsFromTerm(b.term);
          case 'lowestInvestment':
            return a.minimumInvestment - b.minimumInvestment;
          case 'mostFunded':
            const aFunded = (a.intentionAmount + a.closedAmount) / a.maxAmount;
            const bFunded = (b.intentionAmount + b.closedAmount) / b.maxAmount;
            return bFunded - aFunded;
          case 'newest':
          default:
            return new Date(b.publicationDate) - new Date(a.publicationDate);
        }
      });
    },

    extractMonthsFromTerm(term) {
      const match = term.match(/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    },

    viewOpportunityDetail(opportunity) {
      this.$router.push({ 
        name: 'opportunityDetail', 
        params: { id: opportunity.id } 
      });
    },

    handleInvestment(opportunity) {
      this.selectedOpportunity = opportunity;
      this.showInvestmentModal = true;
    },

    async submitInvestment(investmentData) {
      try {
        await api.opportunities.registerIntention(this.selectedOpportunity.id, investmentData);
        this.$store.dispatch("successSnack", "Intención de inversión registrada exitosamente");
        this.showInvestmentModal = false;
        this.selectedOpportunity = null;
        // Reload opportunities and intentions to get updated data
        await Promise.all([
          this.loadOpportunities(),
          this.loadMyIntentions()
        ]);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al registrar la inversión");
      }
    },

    loadMore() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    }
  }
};
</script>